import React, { useMemo } from 'react'
import { Box, MenuItem, TextField, Typography } from '@material-ui/core'
import { useCarrierConfigs } from 'src/api/carrierAnalysis'
import { COHORT_METHOD_OPTIONS, COHORT_RECOMMENDED, COHORT_MAX } from 'src/services/analysisStart'
import { ErrorLine, GroupLabel, Muted, Segmented } from '../components/ui'
import KitSelect, { kitMenuOptions } from '../components/KitSelect'
import { isEnabled } from '../model/features'

// The workflow column's options for the group analyses, under the Analysis tiles.
// Multisample has none: it explains itself. A CNV cohort needs a capture kit, a
// method and a name; Carrier (behind FEATURES.carrier) a panel. Renders nothing for
// Germline and Somatic.

const CONTROL_WIDTH = 352
const selectSx = { width: '100%', maxWidth: CONTROL_WIDTH, '& .MuiInputBase-root': { fontSize: 14.5 } }

const Group = ({ label, children }) => (
  <Box>
    {label && <GroupLabel>{label}</GroupLabel>}
    {children}
  </Box>
)

const RequiredHint = ({ error, showErrors }) => {
  if (!error) return null
  return showErrors ? <ErrorLine>{error}</ErrorLine> : <Muted sx={{ mt: 0.75 }}>Required</Muted>
}

const CohortOptions = ({ workflow, onChange, errors, showErrors, bedFiles }) => {
  const kitOptions = useMemo(() => kitMenuOptions(bedFiles), [bedFiles])
  const setKit = (value) => {
    const kit = kitOptions.find((k) => k.value === value)
    onChange({ kitId: value, kitName: kit?.label || '' })
  }
  return (
    <>
      <Group label="Capture kit">
        <KitSelect
          value={workflow.kitId}
          options={kitOptions}
          onChange={setKit}
          error={showErrors && !!errors.kit}
          fallbackLabel={workflow.kitName}
          sx={selectSx}
        />
        <RequiredHint error={errors.kit} showErrors={showErrors} />
      </Group>
      <Group label="Method">
        <Box sx={{ maxWidth: CONTROL_WIDTH }}>
          <Segmented
            ariaLabel="Method"
            value={workflow.cohortMethod || 'genesus-cnv'}
            options={COHORT_METHOD_OPTIONS}
            onChange={(cohortMethod) => onChange({ cohortMethod })}
          />
        </Box>
      </Group>
      <Group label="Cohort name">
        <TextField
          size="small"
          value={workflow.cohortName || ''}
          placeholder="e.g. CARDIO_2026"
          onChange={(e) => onChange({ cohortName: e.target.value })}
          error={showErrors && !!errors.cohortName}
          inputProps={{ 'aria-label': 'Cohort name', maxLength: 120 }}
          sx={selectSx}
        />
        <RequiredHint error={errors.cohortName} showErrors={showErrors} />
        <Muted sx={{ mt: 0.75 }}>
          {COHORT_RECOMMENDED} samples recommended, {COHORT_MAX} at most.
        </Muted>
      </Group>
    </>
  )
}

const CarrierOptions = ({ workflow, onChange, errors, showErrors }) => {
  const {
    query: { data, isLoading, isError },
  } = useCarrierConfigs()
  const configs = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []
  const current = configs.find((c) => c.id === workflow.carrierConfigId)
  const setConfig = (id) => {
    const config = configs.find((c) => String(c.id) === String(id))
    onChange({
      carrierConfigId: config ? config.id : null,
      carrierConfigTitle: config?.panel_title || config?.title || '',
      carrierSamplesRequired: config ? Number(config.samples_required) || 2 : null,
    })
  }
  return (
    <Group label="Carrier panel">
      <TextField
        select
        size="small"
        value={workflow.carrierConfigId ?? ''}
        onChange={(e) => setConfig(e.target.value)}
        error={showErrors && !!errors.carrierConfig}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) =>
            selected !== '' && selected !== null ? (
              current?.panel_title || workflow.carrierConfigTitle || String(selected)
            ) : (
              <Typography component="span" sx={{ fontSize: 14.5, color: 'text.secondary' }}>
                {isLoading ? 'Loading panels' : 'Choose a panel'}
              </Typography>
            ),
          MenuProps: { PaperProps: { sx: { maxWidth: 440, maxHeight: 400 } } },
        }}
        sx={selectSx}
      >
        {!configs.length && (
          <MenuItem value="" disabled sx={{ fontSize: 14.5 }}>
            {isError ? 'Panels could not be loaded' : 'No panels yet'}
          </MenuItem>
        )}
        {configs.map((c) => (
          <MenuItem key={c.id} value={c.id} sx={{ display: 'block', whiteSpace: 'normal', py: 1 }}>
            <Typography sx={{ fontSize: 14.5 }}>{c.panel_title || c.title}</Typography>
            {c.description && (
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {c.description}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              {c.samples_required} {Number(c.samples_required) === 1 ? 'sample' : 'samples'}
            </Typography>
          </MenuItem>
        ))}
      </TextField>
      <RequiredHint error={errors.carrierConfig} showErrors={showErrors} />
      {current?.description && <Muted sx={{ mt: 0.75, maxWidth: CONTROL_WIDTH }}>{current.description}</Muted>}
    </Group>
  )
}

/**
 * @param onChange  (patch) => void, the panel's workflow updater
 * @param errors    model/workflow validateWorkflow: kit, cohortName, carrierConfig
 */
const GroupColumnOptions = ({ workflow, onChange, errors = {}, showErrors, bedFiles = [] }) => {
  const analysis = workflow?.analysis
  let body = null
  if (analysis === 'multisample') {
    body = (
      <Group label="Multisample">
        <Muted sx={{ maxWidth: CONTROL_WIDTH }}>
          One analysis per group. Finds variants shared or excluded across finished analyses.
        </Muted>
        <Muted sx={{ mt: 0.75 }}>No charge.</Muted>
      </Group>
    )
  } else if (analysis === 'cohort') {
    body = (
      <CohortOptions
        workflow={workflow}
        onChange={onChange}
        errors={errors}
        showErrors={showErrors}
        bedFiles={bedFiles}
      />
    )
  } else if (analysis === 'carrier' && isEnabled('carrier')) {
    body = <CarrierOptions workflow={workflow} onChange={onChange} errors={errors} showErrors={showErrors} />
  }
  if (!body) return null
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{body}</Box>
}

export default GroupColumnOptions
